import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, ClipboardText, Pill, Flame, CaretDown, CaretUp, Lightning } from '@phosphor-icons/react';
import { Md } from '@/components/Md';

const API = process.env.REACT_APP_BACKEND_URL;

export function ProtocolChecklist({ patientId }) {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  const fetch = useCallback(async () => {
    try {
      const [pRes, sRes] = await Promise.all([
        axios.get(`${API}/api/patients/${patientId}/protocol`),
        axios.get(`${API}/api/protocols/suggest/${patientId}`),
      ]);
      setData(pRes.data);
      setSuggestions(sRes.data || []);
    } catch {}
  }, [patientId]);
  useEffect(() => { fetch(); }, [fetch]);

  const toggleItem = async (item) => {
    const current = data?.checklist_status?.[item];
    try {
      await axios.put(`${API}/api/patients/${patientId}/protocol/check-item`, { item, checked: !current?.checked });
      fetch();
    } catch { toast.error('Erro ao atualizar'); }
  };

  const activate = async (protocolId) => {
    try {
      await axios.post(`${API}/api/patients/${patientId}/protocol/activate`, { protocol_id: protocolId });
      toast.success('Protocolo ativado');
      fetch();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };

  const archiveProtocol = async () => {
    try { await axios.post(`${API}/api/patients/${patientId}/protocol/archive`); toast.success('Protocolo arquivado'); fetch(); } catch { toast.error('Erro'); }
  };

  if (!data && suggestions.length === 0) return null;

  const protocol = data?.protocol;
  const cs = data?.checklist_status || {};
  const checklist = protocol?.checklist || [];
  const checkedCount = checklist.filter(item => cs[item]?.checked).length;

  return (
    <div className="bg-white border border-brand-border rounded-2xl overflow-hidden" data-testid="protocol-widget">
      <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center gap-2 p-3 hover:bg-brand-bg/30 text-left">
        <ClipboardText size={16} className="text-brand-green" weight="duotone" />
        <span className="text-xs font-medium text-brand-text flex-1">
          {protocol ? protocol.name : 'Protocolo clínico'}
        </span>
        {protocol && <span className="text-[9px] text-brand-green bg-brand-mint/20 px-2 py-0.5 rounded-full">{checkedCount}/{checklist.length}</span>}
        {expanded ? <CaretUp size={14} className="text-brand-muted" /> : <CaretDown size={14} className="text-brand-muted" />}
      </button>

      {expanded && (
        <div className="border-t border-brand-border p-3">
          {protocol ? (
            <>
              {protocol.eligibility && <p className="text-[10px] text-brand-muted mb-2">{protocol.eligibility}</p>}
              <div className="space-y-1 mb-3">
                {checklist.map((item, i) => {
                  const status = cs[item] || {};
                  return (
                    <button key={i} onClick={() => toggleItem(item)} className="w-full flex items-center gap-2 text-left py-1" data-testid={`check-item-${i}`}>
                      <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${status.checked ? 'bg-brand-green text-white' : 'border border-brand-border'}`}>
                        {status.checked && <Check size={12} weight="bold" />}
                      </div>
                      <span className={`text-xs ${status.checked ? 'text-brand-muted line-through' : 'text-brand-text'}`}>{item}</span>
                      {status.checked_by && <span className="text-[8px] text-brand-placeholder ml-auto">{status.checked_by}</span>}
                    </button>
                  );
                })}
              </div>

              {/* Nutritional targets */}
              {protocol.nutritional_targets && Object.values(protocol.nutritional_targets).some(v => v) && (
                <div className="bg-brand-bg/50 rounded-xl p-2.5 mb-2">
                  <p className="text-[9px] text-brand-muted uppercase tracking-wider mb-1 flex items-center gap-1"><Flame size={10} /> Valores de referência</p>
                  <div className="flex gap-3 text-[10px]">
                    {protocol.nutritional_targets.kcal && <span className="text-brand-text"><b>{protocol.nutritional_targets.kcal}</b> kcal</span>}
                    {protocol.nutritional_targets.proteina_g && <span className="text-brand-text">P: <b>{protocol.nutritional_targets.proteina_g}</b>g</span>}
                    {protocol.nutritional_targets.hidratos_g && <span className="text-brand-text">HC: <b>{protocol.nutritional_targets.hidratos_g}</b>g</span>}
                    {protocol.nutritional_targets.gordura_g && <span className="text-brand-text">G: <b>{protocol.nutritional_targets.gordura_g}</b>g</span>}
                  </div>
                </div>
              )}

              {/* Supplementation */}
              {protocol.supplementation?.filter(s => s.name).length > 0 && (
                <div className="bg-brand-bg/50 rounded-xl p-2.5 mb-2">
                  <p className="text-[9px] text-brand-muted uppercase tracking-wider mb-1 flex items-center gap-1"><Pill size={10} /> Suplementação</p>
                  {protocol.supplementation.filter(s => s.name).map((s, i) => (
                    <Md as="p" key={i} className="text-[10px] text-brand-text">{`${s.name} ${s.dose}${s.notes ? ` (${s.notes})` : ''}`}</Md>
                  ))}
                </div>
              )}

              <button onClick={archiveProtocol} className="text-[10px] text-brand-muted hover:text-red-500 mt-1">Arquivar protocolo</button>
            </>
          ) : suggestions.length > 0 ? (
            <div>
              <p className="text-xs text-brand-muted mb-2">Protocolos sugeridos para este perfil:</p>
              <div className="space-y-1.5">
                {suggestions.map(s => (
                  <button key={s.id} onClick={() => activate(s.id)} className="w-full text-left px-3 py-2 rounded-xl border border-brand-border hover:border-brand-green/30 hover:bg-brand-green/5 transition-all" data-testid={`suggest-${s.id}`}>
                    <p className="text-xs font-medium text-brand-text flex items-center gap-1"><Lightning size={12} className="text-brand-green" /> {s.name}</p>
                    {s.eligibility && <p className="text-[10px] text-brand-muted">{s.eligibility}</p>}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-xs text-brand-muted">Sem protocolo ativo.</p>
          )}
        </div>
      )}
    </div>
  );
}

export function ProtocolHistory({ patientId }) {
  const [history, setHistory] = useState([]);
  useEffect(() => {
    (async () => {
      try { const { data } = await axios.get(`${API}/api/patients/${patientId}/protocol-history`); setHistory(data); } catch {}
    })();
  }, [patientId]);

  if (history.length === 0) return null;

  return (
    <div className="mt-3" data-testid="protocol-history">
      <p className="text-[10px] text-brand-muted uppercase tracking-wider mb-1">Histórico de protocolos</p>
      <div className="space-y-1">
        {history.map(h => {
          const cs = h.checklist_status || {};
          const total = Object.keys(cs).length;
          const checked = Object.values(cs).filter(v => v.checked).length;
          return (
            <div key={h.id} className="flex items-center gap-2 text-[10px] text-brand-muted bg-brand-bg/30 rounded-lg px-2 py-1.5">
              <span className={`w-2 h-2 rounded-full ${h.active ? 'bg-brand-green' : 'bg-brand-border'}`} />
              <span className="flex-1">{h.protocol_name}</span>
              <span>{checked}/{total}</span>
              <span>{h.active ? 'Ativo' : h.archived_at?.split('T')[0] || ''}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
